import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconButton, Link, Stack, Tooltip, Typography } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import PlaceIcon from '@mui/icons-material/Place';
import { navigationHref } from '../utils/navigationHref';

/**
 * A station's address, ready to act on: tappable to hand off to the phone's
 * navigation app, and copyable for everyone else.
 *
 * Why both (#425, Toni T1-8/T1-9): participants are standing in a street trying
 * to reach the next station. Reading an address off a screen and retyping it is
 * the failure mode. The link covers the common case; the copy button is the
 * fallback that works everywhere, including desktop and any browser that does
 * not hand off the URL scheme.
 */

export interface StationAddressProps {
  address: string;
  lat?: number | null;
  lng?: number | null;
  /** Station title — shown as the pin label in the navigation app. */
  label?: string;
  /** Compact rendering for the map popup, where space is tight. */
  dense?: boolean;
}

const StationAddress: React.FC<StationAddressProps> = ({
  address,
  lat,
  lng,
  label,
  dense = false,
}) => {
  const { t } = useTranslation(['runs']);
  const [copied, setCopied] = useState(false);

  const hasCoords = typeof lat === 'number' && typeof lng === 'number';

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(address);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access can be denied (insecure context, permissions). The
      // address stays selectable on screen, so this is a silent no-op rather
      // than an error worth interrupting someone mid-rally with.
    }
  };

  const text = hasCoords ? (
    <Link
      href={navigationHref(lat as number, lng as number, label)}
      underline="hover"
      color="inherit"
      variant={dense ? 'caption' : 'body2'}
      aria-label={t('participant.stationChallenges.address.openInMaps', { address })}
    >
      {address}
    </Link>
  ) : (
    <Typography variant={dense ? 'caption' : 'body2'} color="text.secondary">
      {address}
    </Typography>
  );

  return (
    <Stack
      direction="row"
      spacing={0.5}
      sx={{ alignItems: 'center', color: 'text.secondary' }}
    >
      <PlaceIcon fontSize="small" color="action" />
      {text}
      <Tooltip
        title={t(
          copied
            ? 'participant.stationChallenges.address.copied'
            : 'participant.stationChallenges.address.copy',
        )}
      >
        <IconButton
          size="small"
          onClick={handleCopy}
          aria-label={t('participant.stationChallenges.address.copy')}
        >
          {copied ? (
            <CheckIcon fontSize="inherit" color="success" />
          ) : (
            <ContentCopyIcon fontSize="inherit" />
          )}
        </IconButton>
      </Tooltip>
    </Stack>
  );
};

export default StationAddress;
